import { WalletPlatform } from '../wallets/types';

export interface IOrder {
  /** Which platform issued the order. Decides the badge, and whether it can be managed here. */
  platform: WalletPlatform;
  averagePrice: string;
  executedAt: string | Date;
  externalId: string;
  filledQuantity: string;
  id: number;
  placedAt: string | Date;
  /** The CRM's own position id, for linking. Null when there is no position to link to. */
  positionId: number | null;
  /** The position's id on the issuing platform — the MT5 ticket for an MT5 order. */
  positionExternalId: string | null;
  quantity: string;
  rejectReason: string;
  symbol: string;
  requestPrice: string;
  notionalValue: string | null;
  notionalValueInUSD: string | null;
  status: StatusType;
  type: string;
  limitPrice: string | null;
  stopPrice: string | null;
  takeProfit: string | null;
  stopLoss: string | null;
  side: 'buy' | 'sell';
  iconUrl?: string;
  userIpAddress: string | null;
  trailingStopLossPct: string | null;
  tradingAdapter: string;
}

export interface IOrdersQuery {
  count: number;
  orders: Array<IOrder>;
}

/**
 * A hand-copy of the backend's `OrderStatus` (`cashier/backend/src/orders/types.ts`), plus
 * `accepted`, which only the gateway's reporting API reports. Nothing type-checks the two against
 * each other, and `OrdersFilters` builds its options from `Object.keys` here — so a status missing
 * from this list is a status no one can filter by, and one `OrderStatusBadge` draws with no colour.
 */
export enum StatusType {
  new = 'new',
  filled = 'filled',
  rejected = 'rejected',
  timeout = 'timeout',
  cancelled = 'cancelled',
  pending = 'pending',
  accepted = 'accepted',
  executing = 'executing',
  /** Partially filled, remainder still working. MT5 only — the gateway never reports it. */
  partial = 'partial',
  /** Reached its expiry without filling. MT5 only. */
  expired = 'expired',
}

/**
 * A hand-copy of the backend's `OrderType` (`cashier/backend/src/orders/order.entity.ts`), with the
 * same caveat as {@link StatusType}: the filter options come from `Object.keys` here.
 */
export enum OrderType {
  market = 'market',
  limit = 'limit',
  stop = 'stop',
  /** A stop that places a limit once it triggers. MT5 only. */
  stop_limit = 'stop_limit',
  /** Closes a position against an opposite one rather than at a price. MT5 hedging groups only. */
  close_by = 'close_by',
}

export interface IOrderQueryParams {
  /** Which platform's orders to list. The two are separate tabs, not one merged list. */
  platform?: WalletPlatform;
  limit: number;
  offset: number;
  search: string;
  statuses?: Array<StatusType>;
  types?: Array<OrderType>;
  accountId?: string | null;
  symbols?: Array<string>;
}
